import { BaseClient } from "../../clients/base/BaseClient";
import { CHANNELS_ENDPOINTS } from "./channels.consts";
import type {
  ChannelsResponse,
  ChannelResponse,
  RunningChannelsResponse,
  ChannelPendingCountResponse,
  ChannelStatusResponse,
  UpdateChannelStatusParams,
} from "./channels.types";

/**
 * Channels API Client
 * Handles all channel-related API operations.
 */
export class ChannelsClient extends BaseClient {
  /**
   * Fetch all channels.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<ChannelsResponse>} Array of all channels.
   */
  async fetchChannels(signal?: AbortSignal | null): Promise<ChannelsResponse> {
    return this.get<ChannelsResponse>(CHANNELS_ENDPOINTS.CHANNELS, {
      signal,
    });
  }

  /**
   * Fetch a specific channel by ID.
   * @param {number} id - Channel ID.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<ChannelResponse>} Channel details.
   */
  async fetchChannelById(id: number, signal?: AbortSignal | null): Promise<ChannelResponse> {
    return this.get<ChannelResponse>(CHANNELS_ENDPOINTS.CHANNEL_BY_ID(id), {
      signal,
    });
  }

  /**
   * Fetch all currently running channels.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<RunningChannelsResponse>} Array of running channels.
   */
  async fetchRunningChannels(signal?: AbortSignal | null): Promise<RunningChannelsResponse> {
    return this.get<RunningChannelsResponse>(CHANNELS_ENDPOINTS.RUNNING_CHANNELS, {
      signal,
    });
  }

  /**
   * Fetch pending items count for a specific channel.
   * @param {number} id - Channel ID.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<ChannelPendingCountResponse>} Number of pending items.
   */
  async fetchChannelPendingCount(
    id: number,
    signal?: AbortSignal | null,
  ): Promise<ChannelPendingCountResponse> {
    return this.get<ChannelPendingCountResponse>(CHANNELS_ENDPOINTS.CHANNEL_PENDING(id), {
      signal,
    });
  }

  /**
   * Start or stop a channel.
   * @param {number} id - Channel ID.
   * @param {UpdateChannelStatusParams} params - Status update parameters.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<ChannelStatusResponse>} Updated channel details.
   */
  async updateChannelStatus(
    id: number,
    params: UpdateChannelStatusParams,
    signal?: AbortSignal | null,
  ): Promise<ChannelStatusResponse> {
    return this.put<ChannelStatusResponse>(CHANNELS_ENDPOINTS.CHANNEL_STATUS(id), {
      params,
      signal,
    });
  }

  /**
   * Start a channel (convenience method).
   * @param {number} id - Channel ID.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<ChannelStatusResponse>} Updated channel details.
   */
  async startChannel(id: number, signal?: AbortSignal | null): Promise<ChannelStatusResponse> {
    return this.updateChannelStatus(id, { newStatus: true }, signal);
  }

  /**
   * Stop a channel (convenience method).
   * @param {number} id - Channel ID.
   * @param {AbortSignal | null} signal - Optional abort signal for request cancellation.
   * @returns {Promise<ChannelStatusResponse>} Updated channel details.
   */
  async stopChannel(id: number, signal?: AbortSignal | null): Promise<ChannelStatusResponse> {
    return this.updateChannelStatus(id, { newStatus: false }, signal);
  }
}
